import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, FileText, BookOpen, LayoutTemplate, Megaphone, ArrowLeft, Plus, PenTool } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { getTaskTitle, getTaskFormat, getWordCount } from '../utils/statsUtils';
import Logo from '../components/Logo';
import NavigationDock from '../components/NavigationDock';
import { formatDateTime } from '../utils/timeUtils';

const TEMPLATES = [
  { name: 'Blog Post',       count: 24, icon: FileText },
  { name: 'White Paper',     count: 8,  icon: BookOpen },
  { name: 'Case Study',      count: 12, icon: LayoutTemplate },
  { name: 'Marketing Copy',  count: 31, icon: Megaphone },
];

function StatusBadge({ status }) {
  const styles = {
    completed:   { bg: 'rgba(249,115,22,0.12)',  color: '#F97316', label: 'Published' },
    in_progress: { bg: 'rgba(30,58,138,0.2)',    color: '#93C5FD', label: 'In Progress' },
    not_started: { bg: 'rgba(255,255,255,0.06)', color: '#6B7280', label: 'Draft' },
  };
  const s = styles[status] || styles.not_started;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
      letterSpacing: '0.02em',
    }}>{s.label}</span>
  );
}

export default function TheVault() {
  const navigate = useNavigate();
  const [instances, setInstances] = useState([]);
  const [search, setSearch]       = useState('');
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(8);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    apiClient.get('/workflow-instances/')
      .then(r => setInstances(r.data?.results || r.data || []))
      .catch(() => {});
  }, []);

  const filtered = instances.filter(i => {
    if (!search) return true;
    const title  = getTaskTitle(i).toLowerCase();
    const format = getTaskFormat(i).toLowerCase();
    const q      = search.toLowerCase();
    return title.includes(q) || format.includes(q);
  });

  const visibleInstances = isMobile ? filtered.slice(0, mobileVisibleCount) : filtered;

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: '#0D1117',
      fontFamily: 'Inter, sans-serif',
      overflow: 'hidden',
    }}>
      <style>{`
        .vault-card:hover { background: #1E2640 !important; }
        .vault-card:hover .vault-open { opacity: 1 !important; }
      `}</style>

      {/* Top bar - fixed */}
      <div style={{
        height: isMobile ? 58 : 56,
        minHeight: isMobile ? 58 : 56,
        background: '#111827',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center',
        padding: isMobile ? '0 12px' : '0 24px', gap: isMobile ? 10 : 12,
        flexShrink: 0,
      }}>
        {isMobile ? (
          <>
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#F97316', letterSpacing: '-0.02em' }}>
              The Vault
            </span>
            <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
              Your library
            </span>
          </>
        ) : (
          <>
            <Logo size={28} />
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 15, color: 'white', letterSpacing: '-0.01em' }}>
              Multi Author
            </span>
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.12)' }} />
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, color: '#F97316' }}>
              The Vault
            </span>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate('/task-creation')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '8px 12px' : '6px 14px', background: '#F97316', color: 'white', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, fontFamily: 'Syne, sans-serif', cursor: 'pointer', flexShrink: 0 }}
        >
          + New
        </button>
      </div>

      {/* Scrollable content area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingBottom: 80,
      }}>
        {/* Page header */}
        <div style={{ padding: isMobile ? '20px 16px 0' : '36px 32px 0', maxWidth: 1100, margin: '0 auto', minWidth: 0 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? 26 : 32, color: 'white', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
          The Vault
        </h1>
        <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 28px' }}>
          Every piece your team has created
        </p>

        {/* Search bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#1A2236', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10, padding: isMobile ? '10px 12px' : '10px 14px', marginBottom: 24,
          transition: 'border-color 0.15s',
        }}>
          <Search size={16} color="#6B7280" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search content..."
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 14, color: 'white', background: 'transparent',
            }}
            onFocus={e => e.target.parentElement.style.borderColor = '#F97316'}
            onBlur={e => e.target.parentElement.style.borderColor = 'rgba(255,255,255,0.08)'}
          />
          <button style={{
            display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 7, padding: '5px 10px', fontSize: 12, color: '#6B7280', cursor: 'pointer',
          }}>
            <SlidersHorizontal size={12} />
            Filter
          </button>
        </div>

        {/* Templates */}
        <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, color: 'white', margin: '0 0 14px', letterSpacing: '-0.01em' }}>
          Templates
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)',
          gap: 12, marginBottom: 28,
        }}>
          {TEMPLATES.map(({ name, count, icon: Icon }) => (
            <div
              key={name}
              className="vault-card"
              style={{
                background: '#1A2236',
                border: '1px solid rgba(255,255,255,0.06)',
                borderLeft: '3px solid #1E3A8A',
                borderRadius: 12, padding: 16,
                cursor: 'pointer', transition: 'background 0.15s',
              }}
            >
              <div style={{
                width: isMobile ? 40 : 44, height: isMobile ? 40 : 44, borderRadius: 10,
                background: '#F97316',
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
              }}>
                <Icon size={20} color="white" />
              </div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, color: 'white', marginBottom: 3, overflowWrap: 'anywhere' }}>{name}</div>
              <div style={{ color: '#6B7280', fontSize: 12 }}>{count} templates</div>
            </div>
          ))}
        </div>

        {/* All Content */}
        <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, color: 'white', margin: '0 0 14px', letterSpacing: '-0.01em' }}>
          All Content
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 14,
        }}>
          {filtered.length === 0 ? (
            <div style={{
              gridColumn: isMobile ? 'span 1' : 'span 2',
              textAlign: 'center', color: '#4B5563', padding: 48,
              background: '#1A2236', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12,
            }}>
              No content found.
            </div>
          ) : visibleInstances.map(inst => {
            const title      = getTaskTitle(inst);
            const formatName = getTaskFormat(inst);
            const wordCount  = getWordCount(inst);
            return (
              <div
                key={inst.id}
                className="vault-card"
                style={{
                  background: '#1A2236',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 12, padding: isMobile ? '16px' : '18px 20px',
                  cursor: 'pointer', transition: 'background 0.15s',
                  position: 'relative',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
                onClick={() => navigate('/studio', { state: { workflowInstanceId: inst.id } })}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 8, marginBottom: 10 }}>
                  <StatusBadge status={inst.status} />
                  <span style={{ fontSize: 11, color: '#4B5563', textAlign: 'right' }}>{formatDateTime(inst.created_at)}</span>
                </div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: isMobile ? 15 : 16, color: 'white', marginBottom: 8, lineHeight: 1.3, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                  {title}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10 }}>
                  <div style={{ fontSize: 13, color: '#6B7280', minWidth: 0, overflowWrap: 'anywhere' }}>
                    {wordCount ? `${formatName} · ${wordCount}` : formatName}
                    {inst.status === 'completed' && inst.updated_at && (
                      <span style={{ display: 'block', fontSize: 11, color: '#4B5563', marginTop: 3 }}>
                        Completed {formatDateTime(inst.updated_at)}
                      </span>
                    )}
                  </div>
                  <span className="vault-open" style={{
                    fontSize: 12, color: '#F97316', fontWeight: 700, opacity: isMobile ? 1 : 0,
                    transition: 'opacity 0.15s', flexShrink: 0,
                  }}>
                    Open →
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {isMobile && filtered.length > mobileVisibleCount && (
          <button
            onClick={() => setMobileVisibleCount((count) => count + 8)}
            style={{
              width: '100%',
              marginTop: 16,
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 12,
              color: '#CBD5E1',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Show 8 more pieces
          </button>
        )}
        </div>
      </div>
      {/* End scrollable content */}

      {isMobile ? (
        <NavigationDock />
      ) : (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1A2236', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 999, padding: '8px 20px', display: 'flex', gap: 20, zIndex: 100,
          boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
        }}>
          {[
            { Icon: ArrowLeft, label: 'Dashboard', path: '/dashboard', orange: false },
            { Icon: Plus,      label: 'New',       path: '/task-creation', orange: true },
            { Icon: PenTool,   label: 'Studio',    path: '/studio', orange: false },
          ].map(({ Icon, label, path, orange }) => (
            <button key={label} onClick={() => navigate(path)} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: orange ? '#F97316' : '#6B7280', padding: '2px 4px',
            }}
              onMouseEnter={e => { e.currentTarget.style.color = orange ? '#F97316' : '#F1F5F9'; }}
              onMouseLeave={e => { e.currentTarget.style.color = orange ? '#F97316' : '#6B7280'; }}
            >
              <Icon size={16} />
              <span style={{ fontSize: 10, fontWeight: 600 }}>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
